import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Star, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiRequest, queryClient, toastTaskMutationError } from "@/lib/queryClient";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocation } from "wouter";
import type { Task } from "@shared/schema";

export function TopThreeWidget() {
  const [, setLocation] = useLocation();
  const { data: topThreeTasks = [], isLoading } = useQuery<Task[]>({
    queryKey: ["/api/tasks/top-three"],
  });

  const toggleCompleteMutation = useMutation({
    mutationFn: async ({ id, completed }: { id: string; completed: boolean }) => {
      return apiRequest("PATCH", `/api/tasks/${id}`, { completed });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks/top-three"] });
    },
    onError: (error: Error) => {
      toastTaskMutationError("Couldn't update task", error, "Your change was not saved.");
    },
  });

  const toggleTopThreeMutation = useMutation({
    mutationFn: async (taskId: string) => {
      const response = await apiRequest("POST", `/api/tasks/${taskId}/toggle-top-three`);
      return response.json();
    },
    onSuccess: (result: any) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
        queryClient.invalidateQueries({ queryKey: ["/api/tasks/top-three"] });
      } else {
      }
    },
    onError: (error: any) => {
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500" />
            Top Tasks for Today
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Star className="h-5 w-5 text-yellow-500" fill="#eab308" />
            Top Tasks for Today
        </CardTitle>
      </CardHeader>
      <CardContent>
        {topThreeTasks.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Star className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
            <p className="text-sm">
              No tasks starred for today. Star tasks to prioritize them here.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {topThreeTasks.map((task) => (
              <div
                key={task.id}
                className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={(e) => {
                  // Don't navigate if clicking on checkbox or star button
                  if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('[role="checkbox"]')) {
                    return;
                  }
                  setLocation(`/tasks?edit=${task.id}`);
                }}
                data-testid={`task-item-${task.id}`}
              >
                <Checkbox
                  checked={task.completed}
                  onCheckedChange={(checked) =>
                    toggleCompleteMutation.mutate({ id: task.id, completed: checked as boolean })
                  }
                  className="mt-0.5"
                  data-testid={`checkbox-complete-task-${task.id}`}
                />
                <div className="flex-1 min-w-0">
                  <h4
                    className={`font-medium text-sm ${
                      task.completed ? "line-through text-muted-foreground" : "text-foreground"
                    }`}
                    data-testid={`text-task-title-${task.id}`}
                  >
                    {task.title}
                  </h4>
                  {task.dueDate && (
                    <div 
                      className="flex items-center gap-1 mt-1 text-xs text-muted-foreground"
                      data-testid={`text-task-duedate-${task.id}`}
                    >
                      <Calendar className="h-3 w-3" />
                      <span>{format(new Date(task.dueDate), "MMM d, yyyy")}</span>
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent task from opening when clicking star
                    toggleTopThreeMutation.mutate(task.id);
                  }}
                  data-testid={`button-remove-top-three-${task.id}`}
                   title="Remove star"
                >
                  <Star 
                    className="h-4 w-4" 
                    fill="#f59e0b"
                    style={{ color: "#f59e0b" }}
                  />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
